import { z } from 'zod';

/**
 * Premios que a Grow uP recebeu (decidido pelo GrowuP a 20-10-2026), em
 * `growup.gg/premios`. Gerem-se em Conteudos > Premios > Recebidos
 * (`content.manage`). Os premios que a associacao da aos membros sao o Hall
 * of Fame (`hall-of-fame.ts`). Ver docs/conteudos.
 *
 * Prefixos HTTP:
 *   - `/api/premios`                     site publico (so os publicados);
 *   - `/api/site/content/premios/...`    back-office, so em gestao.
 */

/** Uma imagem carregada (`/api/media/...`); nada de imagens de terceiros. */
const mediaSrcSchema = z
  .string()
  .trim()
  .regex(/^\/api\/media\/[0-9a-f-]{36}\/(sm|md|lg)\.webp$/, 'carregue a imagem no back-office');

/** Vencedor, finalista, nomeado ou mencao honrosa. */
export const AWARD_RESULTS = ['winner', 'finalist', 'nominee', 'mention'] as const;
export const awardResultSchema = z.enum(AWARD_RESULTS);
export type AwardResult = z.infer<typeof awardResultSchema>;

export const AWARD_RESULT_LABELS: Record<AwardResult, string> = {
  winner: 'Vencedor',
  finalist: 'Finalista',
  nominee: 'Nomeado',
  mention: 'Menção honrosa',
};

/** `draft`: so no back-office; `published`: no site. */
export const awardStatusSchema = z.enum(['draft', 'published']);
export type AwardStatus = z.infer<typeof awardStatusSchema>;

/** Um cartao da pagina `/premios`. */
export const publicAwardSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  awardedBy: z.string(),
  year: z.number().int(),
  /** `aaaa-mm-dd` ou `null` (so o ano). */
  awardedOn: z.string().nullable(),
  result: awardResultSchema,
  category: z.string(),
  description: z.string(),
  image: z.object({ url: z.string(), alt: z.string() }).nullable(),
  /** Vazio = sem ligacao. */
  linkUrl: z.string(),
  featured: z.boolean(),
});
export type PublicAward = z.infer<typeof publicAwardSchema>;

/** Uma noticia do site (`/noticias/...`) ou uma pagina de fora em https. */
const linkSchema = z
  .string()
  .trim()
  .max(500)
  .refine((v) => v === '' || /^\/[^/]/.test(v) || /^https:\/\/[^\s]+$/.test(v), {
    message: 'use um endereço do site (/noticias/...) ou um link https://',
  });

export const awardInputSchema = z
  .object({
    title: z.string().trim().min(1, 'escreva o nome do prémio').max(140),
    awardedBy: z.string().trim().min(1, 'escreva quem atribuiu o prémio').max(140),
    year: z.number().int().min(1990).max(2200),
    awardedOn: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .default(null),
    result: awardResultSchema,
    category: z.string().trim().max(120).default(''),
    description: z.string().trim().max(300).default(''),
    imageUrl: mediaSrcSchema.nullable().default(null),
    imageAlt: z.string().trim().max(300).default(''),
    linkUrl: linkSchema.default(''),
    featured: z.boolean().default(false),
  })
  .refine((a) => a.imageUrl === null || a.imageAlt !== '', {
    message: 'descreva a imagem (para quem não a vê)',
    path: ['imageAlt'],
  })
  .refine((a) => a.awardedOn === null || a.awardedOn.startsWith(String(a.year)), {
    message: 'a data tem de ser do ano do prémio',
    path: ['awardedOn'],
  });
export type AwardInput = z.input<typeof awardInputSchema>;

export const adminAwardSchema = publicAwardSchema.omit({ image: true }).extend({
  imageUrl: z.string().nullable(),
  imageAlt: z.string(),
  status: awardStatusSchema,
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Login de quem criou e de quem mexeu por ultimo. */
  createdBy: z.string(),
  updatedBy: z.string(),
});
export type AdminAward = z.infer<typeof adminAwardSchema>;

export const awardOrderRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(1000),
});

const RESULT_RANK: Record<AwardResult, number> = {
  winner: 0,
  finalist: 1,
  nominee: 2,
  mention: 3,
};

/**
 * A ordem do site e do back-office: ano mais recente primeiro; no mesmo ano,
 * vencedor, finalista, nomeado e mencao; no mesmo resultado, a ordem a mao.
 */
export function compareAwards(
  a: { year: number; result: AwardResult; sortOrder: number },
  b: { year: number; result: AwardResult; sortOrder: number },
): number {
  return (
    b.year - a.year || RESULT_RANK[a.result] - RESULT_RANK[b.result] || a.sortOrder - b.sortOrder
  );
}

/** Os premios agrupados por ano, pela ordem de `compareAwards`. */
export function awardsByYear<T extends { year: number }>(
  awards: readonly T[],
): { year: number; awards: T[] }[] {
  const groups: { year: number; awards: T[] }[] = [];
  for (const award of awards) {
    const last = groups.at(-1);
    if (last?.year === award.year) last.awards.push(award);
    else groups.push({ year: award.year, awards: [award] });
  }
  return groups;
}
